import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo1.svg';
import { CONFIG } from '../config/config';
import { useUser } from '../context/UserContext';

const LAST_LOGIN_KEY = 'musiq:last_identifiant';
const LAST_PROFILE_KEY = 'musiq:last_profile_id';

export default function Login() {
  const [identifiant, setIdentifiant] = useState('');
  const [password, setPassword] = useState('');
  // "Se souvenir de moi" activé par défaut — le navigateur ferme la session
  // uniquement si l'utilisateur décoche explicitement
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const { setUser, handleSetEtablissement: setEtablissement, handleSetProfile: setProfile } = useUser();
  const navigate = useNavigate();

  // 1) Redirection si déjà connecté
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) navigate('/arrivee', { replace: true });
  }, [navigate]);

  // 2) Pré-remplir le dernier identifiant utilisé
  useEffect(() => {
    const last = localStorage.getItem(LAST_LOGIN_KEY);
    if (last) setIdentifiant(last);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await fetch(`${CONFIG.API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifiant, password }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Échec de la connexion');
        return;
      }

      // Mémoriser le dernier identifiant (toujours, pour le pré-remplissage)
      localStorage.setItem(LAST_LOGIN_KEY, identifiant.trim());

      const storage = remember ? localStorage : sessionStorage;

      // Stockage du token
      storage.setItem('token', data.token);

      // Récupération des infos utilisateur (/me) — on transmet le dernier profil utilisé
      const lastProfileId = localStorage.getItem(LAST_PROFILE_KEY) || '';
      const meUrl = lastProfileId
        ? `${CONFIG.API_URL}/auth/me?lastProfileId=${encodeURIComponent(lastProfileId)}`
        : `${CONFIG.API_URL}/auth/me`;
      const meRes = await fetch(meUrl, {
        headers: { Authorization: `Bearer ${data.token}` },
      });

      const user = await meRes.json();
      if (!meRes.ok) {
        setError('Connexion réussie, mais erreur de récupération des infos.');
        return;
      }

      // Stockage de l'utilisateur + rôle
      storage.setItem('user', JSON.stringify(user));
      storage.setItem('role', user.role || 'infirmiere');
      storage.setItem('profile', JSON.stringify(user.initProfile));
      storage.setItem('etablissement', JSON.stringify(user.initProfile.initEtab));
      setUser(user);
      setProfile(user.initProfile);
      setEtablissement(user.initProfile.initEtab);

      // Redirection
      navigate('/arrivee', { replace: true });
    } catch (err) {
      console.error('Erreur login:', err);
      setError('Erreur réseau ou serveur.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#A51A1F] to-[#DB0E16]">
      {/* Header */}
      <header className="flex-none flex flex-col items-center px-4 pt-10 bg-transparent">
        <img
          src={logo}
          alt="Mon Urgence Santé"
          className="mb-4 w-48 max-w-[60vw] h-auto object-contain"
        />
      </header>

      {/* Formulaire */}
      <main className="flex-grow flex items-center justify-center px-4">
        <div className="w-full max-w-xs">
          <form onSubmit={handleSubmit} className="flex flex-col rounded-lg space-y-6 p-6">
            {error && <p className="text-center text-sm text-white">{error}</p>}

            <input
              type="text"
              id="login-identifiant"
              name="username"
              placeholder="Identifiant"
              value={identifiant}
              onChange={(e) => setIdentifiant(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              className="block w-full p-3 border border-gray-200 rounded bg-white text-center text-red-600 placeholder-red-400 focus:ring-2 focus:ring-red-400"
              required
            />

            <input
              type="password"
              id="login-password"
              name="current-password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="current-password"
              className="block w-full p-3 border border-gray-200 rounded bg-white text-center text-red-600 placeholder-red-400 focus:ring-2 focus:ring-red-400"
              required
            />

            <label
              htmlFor="login-remember"
              className="flex items-center justify-center space-x-2 text-white text-sm"
            >
              <input
                id="login-remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 text-red-600 border-gray-300 rounded bg-white"
              />
              <span className="font-thin">Se souvenir de moi</span>
            </label>

            <button
              type="submit"
              className="w-full bg-white text-red-600 py-3 font-semibold rounded hover:opacity-90 transition"
            >
              Connexion
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="flex-none">
        <hr className="border-t border-white w-5/6 mx-auto" />
        <div className="flex justify-between px-6 py-4 text-white text-sm font-thin">
          <button type="button" className="hover:text-red-200">
            Mot de passe oublié
          </button>
          <button type="button" className="hover:text-red-200">
            Contacter un admin
          </button>
        </div>
      </footer>
    </div>
  );
}
